import { SubstituteSchedulerLesson } from "../../components/scheduler/SubstituteScheduler";
import {
  ILesson,
  ScheduleLesson,
  SchedulerLesson,
  VacancyLesson,
} from "../../models/schedule/lesson";
import { Schedule } from "../../models/schedule/schedule";
import { mod } from "./math.util";
import { v4 as uuidv4 } from "uuid";

export type ExtendedLesson = {
  canActivate: boolean;
} & ScheduleLesson;

export const weekdays = [
  { id: 0, name: "Montag", shortName: "Mo" },
  { id: 1, name: "Dienstag", shortName: "Di" },
  { id: 2, name: "Mittwoch", shortName: "Mi" },
  { id: 3, name: "Donnerstag", shortName: "Do" },
  { id: 4, name: "Freitag", shortName: "Fr" },
];

/**
 * Returns the day of a week (0 = Monday, 1 = Tuesday etc.)
 * @param date The date to get the weekday from
 * @returns Weekday with id, name and shortname
 */
export function getWeekDay(date: Date) {
  return weekdays[mod(date.getDay() - 1, 5)];
}

/**
 * Returns the day of a week (0 = Monday, 1 = Tuesday etc.)
 * @param startDate The date to get the weekday from
 * @param idx the offset to get the weekday from (Monday + 2 = Wednesday)
 * @returns Weekday with id, name and shortname
 */
export function getWeekdayWithId(startDate: Date, idx: number) {
  return weekdays[mod(startDate.getDay() + idx - 1, 5)];
}

export function calculateBusinessDays(startDate: Date, endDate: Date) {
  const startDateClone = new Date(startDate);
  const endDateClone = new Date(endDate);
  // Validate input
  if (endDateClone < startDateClone) return 0;

  // Calculate days between dates
  const millisecondsPerDay = 86400 * 1000; // Day in milliseconds
  startDateClone.setHours(0, 0, 0, 1); // Start just after midnight
  endDateClone.setHours(23, 59, 59, 999); // End just before midnight

  const vTimezoneDiff =
    endDateClone.getTimezoneOffset() - startDateClone.getTimezoneOffset();
  if (vTimezoneDiff !== 0) {
    // Handle daylight saving time difference between two dates.
    startDateClone.setMinutes(startDateClone.getMinutes() + vTimezoneDiff);
  }

  const diff = endDateClone.getTime() - startDateClone.getTime(); // Milliseconds between datetime objects
  let days = Math.ceil(diff / millisecondsPerDay);

  // Subtract two weekend days for every week in between
  const weeks = Math.floor(days / 7);
  days = days - weeks * 2;

  // Handle special cases
  const startDay = startDateClone.getDay();
  const endDay = endDateClone.getDay();

  // Remove weekend not previously removed.
  if (startDay - endDay > 1) days = days - 2;

  // Remove start day if span starts on Sunday but ends before Saturday
  if (startDay === 0 && endDay !== 6) {
    days = days - 1;
  }

  // Remove end day if span ends on Saturday but starts after Sunday
  if (endDay === 6 && startDay !== 0) {
    days = days - 1;
  }

  return days;
}

export function generateComponentSchedule(
  schedule: Schedule,
  start: Date,
  end: Date,
  initialLessons?: VacancyLesson[],
  thresholdDate?: Date,
): SchedulerLesson[][] {
  const startDay = mod(start.getDay() - 1, 7);
  const endDay = mod(end.getDay() - 1, 7);
  const duration = calculateBusinessDays(start, end);

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  thresholdDate?.setHours(0, 0, 0, 0);

  if (start.getTime() > end.getTime()) {
    throw new Error("Start date has to be before the end date");
  }
  if ([5, 6].includes(startDay)) {
    throw new Error("Start date cannot be saturday or sunday");
  }
  if ([5, 6].includes(endDay)) {
    throw new Error("End date cannot be saturday or sunday");
  }

  const lessons: SchedulerLesson[][] = [];

  const currentDate = new Date(start);
  currentDate.setDate(currentDate.getDate() - startDay);
  currentDate.setHours(0, 0, 0, 0);

  // If generate whole week is activated the week has to be filled up until the next friday
  // duration + the difference from the startday to the number of working days (5)
  const endIndex = duration + (4 - endDay) + startDay;
  for (let i = 0; i < endIndex; i++) {
    lessons.push([]);
    const weekday = mod(i, 5);
    // LessonIndex for whole weeks starts at 0 otherwise i is the startDay and has to be subtracted to get to 0
    const lessonIndex = i;
    const durationIndex = duration + startDay;
    for (let j = 0; j < schedule.duration.length; j++) {
      const scheduleLesson = schedule.lessons.find(
        (lesson) =>
          lesson.dayCode === weekday &&
          lesson.lessonCode === j &&
          i < durationIndex &&
          i >= startDay,
      );
      const initialLesson = initialLessons?.find(
        (l) =>
          // lessonIndex is 0 for the first lesson, this can be any day of the week so we have to subtract the startDay when generating whole weeks
          l.lessonIndex === lessonIndex &&
          l.dayCode === weekday &&
          l.lessonCode === j &&
          i < durationIndex &&
          i >= startDay,
      );
      const lessonActive =
        !!initialLesson ||
        (thresholdDate &&
          thresholdDate.getTime() < currentDate.getTime() &&
          !!scheduleLesson);
      lessons[lessonIndex].push({
        ...initialLesson,
        lessonId: scheduleLesson?.id,
        dayCode: weekday,
        lessonCode: j,
        lessonIndex: lessonIndex,
        isActive: lessonActive || (!!scheduleLesson && !initialLessons),
        canActivate: !!scheduleLesson,
        date: new Date(currentDate),
        name: scheduleLesson?.name,
        id: initialLesson?.id ?? uuidv4(),
        subSchool: scheduleLesson?.subSchool,
      });
    }
    currentDate.setDate(currentDate.getDate() + (weekday === 4 ? 3 : 1));
    currentDate.setHours(0, 0, 0, 0);
  }

  return lessons;
}

export function generateSubstituteComponentSchedule(
  schedule: Schedule,
  start: Date,
  end: Date,
  initialLessons?: VacancyLesson[],
  thresholdDate?: Date,
): SubstituteSchedulerLesson[][] {
  const lessons = generateComponentSchedule(
    schedule,
    start,
    end,
    initialLessons,
    thresholdDate,
  );
  return lessons.map((dayLessons) =>
    dayLessons.map((lesson) => {
      const initialLesson = initialLessons?.find(
        (l) =>
          l.lessonIndex === lesson.lessonIndex &&
          l.lessonCode === lesson.lessonCode,
      );
      return {
        ...lesson,
        teacherAccepted: undefined,
        selectedSubstitute: initialLesson?.teacherAccepted,
      };
    }),
  );
}

/**
 * Patches changes on lessons onto an exsiting component schedule list
 * @param componentSchedule
 * @param changedLessons
 * @returns
 */
export function patchComponentSchedule<T extends ILesson>(
  componentSchedule: T[][],
  changedLessons: T[],
): T[][] {
  const clone: T[][] = [];
  for (let day = 0; day < componentSchedule.length; day++) {
    clone.push([]);
    for (let lesson = 0; lesson < componentSchedule[day].length; lesson++) {
      const changedLesson = changedLessons.find(
        (l) => l.lessonIndex === day && l.lessonCode === lesson,
      );
      if (changedLesson) {
        clone[day].push({ ...changedLesson });
      } else {
        clone[day].push({ ...componentSchedule[day][lesson] });
      }
    }
  }
  return clone;
}

/**
 * Removes the time and compares only the date of 2 date objects
 */
export function compareDates(
  date1: Date,
  date2: Date,
  compare: (a: number, b: number) => boolean,
): boolean {
  return compare(date1.setHours(0, 0, 0, 0), date2.setHours(0, 0, 0, 0));
}

/**
 * Checks if the given date is in the current week (sunday to sunday)
 * @param date
 * @returns
 */
export function isDateInThisWeek(date: Date) {
  date = new Date(date);
  const todayObj = new Date();
  const todayDate = todayObj.getDate();
  const todayDay = todayObj.getDay();

  // get first date of week
  const firstDayOfWeek = new Date(todayObj.setDate(todayDate - todayDay));

  // get last date of week
  const lastDayOfWeek = new Date(firstDayOfWeek);
  lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6);

  // if date is equal or within the first and last dates of the week
  const t = date >= firstDayOfWeek && date <= lastDayOfWeek;
  return t;
}

export const isWeekendDay = (date: Date) => {
  return [0, 6].includes(new Date(date).getDay());
};
